import {TileApplications} from "@spg/component_library";
import fetchResolver, {Resolution} from "./FetchResolver";
import {
  AppDomainResponse,
  AvailableAppDetailsResponse,
  AvailableAppParametersResponse,
  AvailableAppResponse,
  CoreAppResponse,
  InstalledAppDetailsResponse,
  InstalledAppResponse,
  AppStatusResponse,
  WikiPage,
  CoreAppDetailsResponse,
  GroupDetailResponse,
  GroupCreationResponse,
  GroupResponse,
  GroupMemberResponse,
  MyAppsResponse,
  GroupMemberRequest,
} from "./APITypes";

/** Provides an interface to operations in the backend */
const API = {
  /** Attempts to get all applications from the portal backend */
  getApplications: async (): Promise<Resolution<TileApplications>> => {
    return fetchResolver.apiCall({path: "apps", backend: "portal"});
  },

  /** Attempts to get all installed applications of a group */
  getInstalledApps: async (
    groupId: string,
  ): Promise<Resolution<InstalledAppResponse[]>> => {
    return fetchResolver.apiCall({path: `groups/${groupId}/apps/installed`});
  },

  /** Attempts to get all core applications */
  getCoreApps: async (): Promise<Resolution<CoreAppResponse[]>> => {
    return fetchResolver.apiCall({path: "apps/core"});
  },

  /** Attempts to get all available applications of a group */
  getAvailableApps: async (
    groupId: string,
  ): Promise<Resolution<AvailableAppResponse[]>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/apps/available`,
      includeLocale: true,
    });
  },

  /** Attempts to get the details of an installed application */
  getInstalledApp: async (
    groupId: string,
    appId: string,
  ): Promise<Resolution<InstalledAppDetailsResponse>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/apps/installed/${appId}`,
      includeLocale: true,
    });
  },

  /** Attempts to get the details of a core application */
  getCoreApp: async (
    id: string,
  ): Promise<Resolution<CoreAppDetailsResponse>> => {
    return fetchResolver.apiCall({
      path: "apps/core/" + id,
      includeLocale: true,
    });
  },

  /** Attempts to get the details of an available application */
  getAvailableApp: async (
    groupId: string,
    appId: string,
  ): Promise<Resolution<AvailableAppDetailsResponse>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/apps/available/${appId}`,
      includeLocale: true,
    });
  },

  /** Attempts to get an associated app domain to a specific application */
  getAppDomain: async (id: string): Promise<Resolution<AppDomainResponse>> => {
    return fetchResolver.apiCall({
      path: `apps/${id}/app_domain`,
      backend: "datamarket",
    });
  },

  /** Attempts to get the content of a specific wiki page by path */
  getWikiPageContent: async (
    pagePath: string,
  ): Promise<Resolution<WikiPage>> => {
    return fetchResolver.apiCall({
      path: "",
      backend: "wiki",
      method: "POST",
      body: {
        query: `query Pages { pages { singleByPath (path: "${pagePath}", locale: "de") { content } } }`,
      },
    });
  },

  /** Attempts to get the parameters required for the installation of an app */
  getAppParameters: async (
    groupId: string,
    appId: string,
  ): Promise<Resolution<AvailableAppParametersResponse>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/apps/available/${appId}/parameters`,
      includeLocale: true,
    });
  },

  /** Attempts to install an app */
  installApp: async (
    groupId: string,
    appId: string,
    abortController: AbortController,
    parameters?: object,
  ): Promise<Resolution<null>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/apps/available/${appId}`,
      method: "POST",
      body: parameters,
      abortController: abortController,
    });
  },

  /** Attempts to update an app */
  updateApp: async (
    groupId: string,
    appId: string,
  ): Promise<Resolution<null>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/apps/installed/${appId}`,
      method: "PATCH",
    });
  },

  /** Attempts to uninstall an app */
  uninstallApp: async (
    groupId: string,
    appId: string,
  ): Promise<Resolution<null>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/apps/installed/${appId}`,
      method: "DELETE",
    });
  },

  /** Attempts to cancel an installation of an app */
  cancelInstallApp: async (
    groupId: string,
    appId: string,
  ): Promise<Resolution<null>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/apps/installing/${appId}`,
      method: "DELETE",
    });
  },

  /** Attempts to get the status of an app */
  getAppStatus: async (
    groupId: string,
    appId: string,
    appInstalled: boolean,
  ): Promise<Resolution<AppStatusResponse>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/apps/${appInstalled ? "installed" : "available"}/${appId}/status`,
    });
  },

  /** Attempts to get all groups */
  getGroups: async (options?: {
    isMember?: boolean;
  }): Promise<Resolution<GroupResponse[]>> => {
    const params = options?.isMember ? "?is_member=true" : "";
    return fetchResolver.apiCall({path: `groups${params}`});
  },

  /** Attempts to create a new group */
  createGroup: async (
    name: string,
    slug: string,
    description: string,
    admins: string[],
  ): Promise<Resolution<GroupCreationResponse>> => {
    return fetchResolver.apiCall({
      path: "groups",
      method: "POST",
      body: {name, slug, description, admin_emails: admins},
    });
  },

  /** Attempts to create a new group */
  updateGroup: async (
    groupId: string,
    name: string,
    description: string,
  ): Promise<Resolution<GroupCreationResponse>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}`,
      method: "PATCH",
      body: {name, description},
    });
  },

  /** Attempts to delete a group */
  deleteGroup: async (groupId: string): Promise<Resolution<null>> => {
    return fetchResolver.apiCall({
      path: "groups/" + groupId,
      method: "DELETE",
    });
  },

  /** Attempts to get the details of a group */
  getGroup: async (
    groupId: string,
  ): Promise<Resolution<GroupDetailResponse>> => {
    return fetchResolver.apiCall({path: "groups/" + groupId});
  },

  /** Attempts to get the members of a group */
  getGroupMembers: async (
    groupId: string,
  ): Promise<Resolution<GroupMemberResponse[]>> => {
    return fetchResolver.apiCall({path: "groups/" + groupId + "/members"});
  },

  /** Attempts to add members to a group */
  addGroupMembers: async (
    groupId: string,
    members: GroupMemberRequest[],
  ): Promise<Resolution<null>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/members`,
      method: "POST",
      body: {members},
    });
  },

  /** Attempts to remove a member from a group */
  removeGroupMember: async (
    groupId: string,
    userId: string,
  ): Promise<Resolution<null>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/members/${userId}`,
      method: "DELETE",
    });
  },

  /** Attempts to update a member from a group */
  updateGroupMember: async (
    groupId: string,
    userId: string,
    isAdmin: boolean,
  ): Promise<Resolution<null>> => {
    return fetchResolver.apiCall({
      path: `groups/${groupId}/members/${userId}`,
      method: "PATCH",
      body: {is_admin: isAdmin},
    });
  },

  /** Attempts to get all apps of groups the user is a member of */
  getMyApps: async (): Promise<Resolution<MyAppsResponse[]>> => {
    return fetchResolver.apiCall({
      path: "groups/me/apps/installed",
    });
  },
};

export default API;
export {type Resolution};
